import { Mail, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { ImportInviteReport, ImportInviteStatus } from '../../types/api'
import { AdminHeader, BulkUploadTabs } from './adminAccess'

const STATUS_LABEL: Record<ImportInviteStatus, string> = {
  DRAFT: 'Draft',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
}
const STATUS_TONE: Record<ImportInviteStatus, string> = {
  DRAFT: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-ink-100 text-ink-600',
}

/** "Not emailed" first: drafts waiting for someone to pick them is what this page is mostly for. */
type Filter = 'NOT_EMAILED' | 'ALL' | ImportInviteStatus
const FILTERS: Filter[] = ['NOT_EMAILED', 'ALL', 'DRAFT', 'CONFIRMED', 'REJECTED']
const FILTER_LABEL: Record<Filter, string> = { NOT_EMAILED: 'Not emailed', ALL: 'All', ...STATUS_LABEL }

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-'

/** STUDENT -> Seeker, TUTOR -> Tutor. */
const kindLabel = (kind: string) => (kind === 'STUDENT' ? 'Seeker' : kind.charAt(0) + kind.slice(1).toLowerCase())

/**
 * The bulk upload report (V51): everyone an admin or an aggregator uploaded. An upload sends no
 * email - every row is a draft (no dot, no sign-in) until someone here ticks it and presses "Send
 * invitation". Then they are emailed Confirm / Reject: confirm makes the account live with a dot,
 * reject deletes it. Super admin, admin and creator can all send.
 */
export function AdminBulkUploadsPage() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('NOT_EMAILED')
  const [page, setPage] = useState(0)
  const [report, setReport] = useState<ImportInviteReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Ticked rows - drafts only, on the page in view; cleared whenever the list changes.
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [sending, setSending] = useState(false)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const fail = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) signOut()
      else setError(apiErrorMessage(err, fallback))
    },
    [signOut],
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const status = filter === 'ALL' || filter === 'NOT_EMAILED' ? undefined : filter
      setReport(await adminApi.importInvites(status, filter === 'NOT_EMAILED', page))
      setPicked(new Set())
    } catch (err) {
      fail(err, 'Could not load the report')
    } finally {
      setLoading(false)
    }
  }, [filter, page, fail])

  useEffect(() => {
    void load()
  }, [load])

  async function send(ids: string[]) {
    if (ids.length === 0) return
    setSending(true)
    setNotice(null)
    setError(null)
    try {
      const { sent, skipped } = await adminApi.sendImportInvites(ids)
      setNotice(
        `Invitation sent to ${sent} ${sent === 1 ? 'person' : 'people'}.` +
          (skipped > 0 ? ` ${skipped} skipped - they had already answered.` : ''),
      )
      await load()
    } catch (err) {
      fail(err, 'Could not send the invitations')
    } finally {
      setSending(false)
    }
  }

  const drafts = (report?.rows ?? []).filter((r) => r.status === 'DRAFT')
  const allPicked = drafts.length > 0 && drafts.every((r) => picked.has(r.id))
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const counts = report?.counts
  const totalAll = counts ? counts.DRAFT + counts.CONFIRMED + counts.REJECTED : undefined
  const lastPage = report ? Math.max(0, Math.ceil(report.total / report.size) - 1) : 0

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="bulkUpload" onSignOut={signOut} width="max-w-6xl">
        <button onClick={() => void load()} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <BulkUploadTabs />
        <div>
          <h1 className="text-xl font-bold text-ink-900">Report status</h1>
          <p className="mt-1 text-sm text-ink-500">
            Unless "Email everyone" was ticked on the upload, it sends no email: everyone stays a draft, with no dot
            and no sign-in. Tick who should be invited and press Send invitation. Their dot appears once they confirm; a reject deletes the account.
          </p>
        </div>

        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {[
            { label: 'Uploaded', value: totalAll },
            { label: 'Not emailed', value: report?.notEmailed, tone: 'text-red-700' },
            { label: 'Draft', value: counts?.DRAFT, tone: 'text-amber-700' },
            { label: 'Confirmed', value: counts?.CONFIRMED, tone: 'text-green-700' },
            { label: 'Rejected', value: counts?.REJECTED },
          ].map((t) => (
            <li key={t.label} className="card-pad">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">{t.label}</div>
              <div className={`mt-1 text-2xl font-bold ${t.tone ?? 'text-ink-900'}`}>{t.value ?? '-'}</div>
            </li>
          ))}
        </ul>

        <ErrorMessage message={error} />
        {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => {
                  setFilter(f)
                  setPage(0)
                }}
                aria-pressed={filter === f}
                className={`rounded-full px-3 py-1 text-sm font-semibold ${
                  filter === f ? 'bg-ink-900 text-ink-50' : 'bg-white text-ink-600 hover:text-ink-900'
                }`}
              >
                {FILTER_LABEL[f]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void send([...picked])}
            disabled={picked.size === 0 || sending}
            className="btn-sun gap-1.5 disabled:opacity-40"
          >
            <Mail size={15} />
            {sending ? 'Sending...' : `Send invitation${picked.size > 0 ? ` (${picked.size})` : ''}`}
          </button>
        </div>

        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select every draft on this page"
                    checked={allPicked}
                    disabled={drafts.length === 0}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(drafts.map((r) => r.id)))}
                  />
                </th>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Uploaded by</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3">Email sent</th>
                <th className="px-4 py-3">Answered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {!report || report.rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-ink-500">
                    {loading ? 'Loading...' : filter === 'NOT_EMAILED' ? 'Everyone uploaded has been emailed.' : 'Nobody here yet.'}
                  </td>
                </tr>
              ) : (
                report.rows.map((r) => (
                  <tr key={r.id} className={`align-top ${picked.has(r.id) ? 'bg-sun-100/50' : ''}`}>
                    <td className="px-4 py-3">
                      {r.status === 'DRAFT' && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.fullName}`}
                          checked={picked.has(r.id)}
                          onChange={() => toggle(r.id)}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink-900">{r.fullName}</div>
                      <div className="break-all text-xs text-ink-600">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-ink-700">{kindLabel(r.entityKind)}</td>
                    <td className="px-4 py-3 text-ink-700">{r.uploadedBy ?? '-'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[r.status]}`}
                      >
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-600">{fmt(r.uploadedAt)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-600">
                      {r.emailSentAt ? fmt(r.emailSentAt) : <span className="font-medium text-red-700">Not emailed</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-600">{fmt(r.respondedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {report && report.total > report.size && (
          <div className="flex items-center justify-between text-sm text-ink-600">
            <span>
              Page {page + 1} of {lastPage + 1} · {report.total} people
            </span>
            <div className="flex gap-2">
              <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button className="btn-ghost" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
